import React, { useEffect, useState } from "react";
import { Table, Spinner, Alert, Input, Button, Form, FormGroup, Label } from "reactstrap";
import axiosInstance from "../../request";
import { URL } from "../../api";

const DisbursalReporting = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  // Filter States
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loanId, setLoanId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [filterType, setFilterType] = useState("loanId");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 25;
  const [hasNextPage, setHasNextPage] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});

  const companyCode = localStorage.getItem("companyCode");
const branchCode = localStorage.getItem("branch_code");

  useEffect(() => {
    fetchDisbursals(1, {});
  }, []);

  const fetchDisbursals = async (page = 1, filters = {}) => {
    setLoading(true);
    setError("");
    try {
      const params = { ...filters };
      params.page = page - 1; // Backend zero-based
      params.size = rowsPerPage;

      if (companyCode) params.companyCode = companyCode;
      if (branchCode) params.branchCode = branchCode;

      const res = await axiosInstance.get(URL.DISBURSAL_REPORTING, { params });
      
      // Ensure we extract the array correctly depending on API response structure
      const resultData = res.data.content || (Array.isArray(res.data) ? res.data : []);

      setData(resultData);
      setCurrentPage(page);
      setHasNextPage(resultData.length === rowsPerPage);
    } catch (err) {
      setError("Failed to fetch disbursal reporting data.");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    const filters = {};
    if (fromDate) filters.fromDate = fromDate;
    if (toDate) filters.toDate = toDate;
    if (filterType === "customerId" && customerId) filters.customerId = customerId;
    if (filterType === "loanId" && loanId) filters.loanId = loanId;

    setActiveFilters(filters);
    fetchDisbursals(1, filters);
  };

  const handleExcelExport = async () => {
    if (data.length === 0) return alert("No data available to export!");
    setExporting(true);
    try {
      const params = { ...activeFilters, export: true };
      if (companyCode) params.companyCode = companyCode;
      if (branchCode) params.branchCode = branchCode;
      const response = await axiosInstance.get(URL.DISBURSAL_REPORTING, {
        params,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Disbursal_Report_${new Date().getTime()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Export failed.");
    } finally {
      setExporting(false);
    }
  };

  // ✅ Totals logic for the visible page
const pageTotals = data.reduce((acc, row) => {
  acc.loan_amount += Number(row.loan_amount || 0);
  acc.demand += Number(row.demand || 0);
  acc.disbursal_amt += Number(row.disbursal_amt || 0);
  acc.total_fess += Number(row.total_fess || 0);

  return acc;
}, {
  loan_amount: 0,
  demand: 0,
  disbursal_amt: 0,
  total_fess: 0
});

  const font10 = { fontSize: "10px" };
  const inputSmStyle = { fontSize: "11px", height: "30px", padding: "2px 6px" };
  const buttonSmStyle = { fontSize: "11px", padding: "4px 10px", height: "30px", lineHeight: "1" };

  return (
    <div style={font10}>
      <Form onSubmit={handleSearch} className="mb-3">
        <div className="d-flex align-items-end flex-wrap" style={{ gap: "10px" }}>
          <FormGroup className="mb-0">
            <Label className="mb-1 fw-bold">Filter Type</Label>
            <Input type="select" bsSize="sm" value={filterType}
              onChange={(e) => { setFilterType(e.target.value); setLoanId(""); setCustomerId(""); }}
              style={inputSmStyle}>
              <option value="loanId">Loan ID</option>
              <option value="customerId">Customer ID</option>
            </Input>
          </FormGroup>

          <FormGroup className="mb-0">
            <Label className="mb-1 fw-bold">{filterType === "customerId" ? "Customer ID" : "Loan ID"}</Label>
            <Input bsSize="sm" type="number" value={filterType === "customerId" ? customerId : loanId}
              onChange={(e) => filterType === "customerId" ? setCustomerId(e.target.value) : setLoanId(e.target.value)}
              style={inputSmStyle} />
          </FormGroup>

          <FormGroup className="mb-0">
            <Label className="mb-1 fw-bold">From Date</Label>
            <Input type="date" bsSize="sm" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={inputSmStyle} />
          </FormGroup>

          <FormGroup className="mb-0">
            <Label className="mb-1 fw-bold">To Date</Label>
            <Input type="date" bsSize="sm" value={toDate} onChange={(e) => setToDate(e.target.value)} style={inputSmStyle} />
          </FormGroup>

          <div className="d-flex" style={{ gap: "6px" }}>
            <Button color="primary" type="submit" style={buttonSmStyle}>Search</Button>
            <Button color="success" onClick={handleExcelExport} style={buttonSmStyle} disabled={exporting}>
              {exporting ? <Spinner size="sm" /> : "Excel"}
            </Button>
          </div>
        </div>
      </Form>

      {loading ? (
        <div className="text-center p-4"><Spinner size="sm" /> Loading...</div>
      ) : error ? (
        <Alert color="danger" style={font10}>{error}</Alert>
      ) : (
        <>
          <div style={{ overflowX: "auto" }}>
            <Table bordered striped hover responsive size="sm" className="mt-2 mb-0 text-nowrap">
              <thead className="thead-light">
                <tr>
                  <th>#</th>
                  <th>Customer ID</th>
                  <th>Full Name</th>
                  <th>PAN</th>
                  <th>Loan ID</th>
                  <th>Loan Amount</th>
                  <th>Demand</th>
                  <th>Fintech Name</th>
                  <th>Total GST</th>
                  <th>Proc. Fee</th>
                  <th>Total Fees</th>
                  <th>Repayment Type</th>
                  <th>ROI</th>
                  <th>ROI Type</th>
                  <th>Tenure</th>
                  <th>Repayment Date</th>
                  <th>Product</th>
                  <th>Approved Date</th>
                  <th>Disbursed Date</th>
                  <th>Closed Date</th>
                  <th>Loan Status</th>
                  <th>Bank Name</th>
                  <th>IFSC</th>
                  <th>Account No</th>
                  <th>Reference No</th>
                  <th>Disbursed Amount</th>
                  <th>Approved By</th>
                  <th>Pin Code</th>
                  <th>Address</th>
                  <th>State</th>
                  <th>City</th>
                  <th>Alternate No</th>
                  <th>Company Code</th>
                  <th>Agency Code</th>
                </tr>
              </thead>
              <tbody>
                {data.length > 0 ? (
                  <>
                    {data.map((row, idx) => (
                      <tr key={idx}>
                        <td>{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                        <td>{row.customer_id}</td>
                        <td>{row.full_name}</td>
                        <td>{row.pan_number}</td>
                        <td>{row.loan_id}</td>
                        <td>
  ₹{Number(row.loan_amount || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2
  })}
</td>

<td>
  ₹{Number(row.demand || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2
  })}
</td>

<td>{row.fintech_name}</td>
                        <td>₹{Number(row.total_gst || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                        <td>₹{Number(row.processing_fee || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                        <td>₹{Number(row.total_fess || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                        <td>{row.repayment_type}</td>
                        <td>{row.roi}%</td>
                        <td>{row.roi_type}</td>
                        <td>{row.tenure}</td>
                        <td>{row.repayment_date}</td>
                        <td>
                          {row.loan_amount && row.tenure
                            ? `${Math.round(row.loan_amount / 1000)}K${row.tenure}`
                            : row.product_name}
                        </td>
                        <td>{row.loan_approved_date}</td>
                        <td>{row.loan_disbursed_date}</td>
                        <td>{row.loan_closed_date}</td>
                        <td>{row.loan_status}</td>
                        <td>{row.bank_name}</td>
                        <td>{row.ifsc_code}</td>
                        <td>{row.account_number}</td>
                        <td>{row.reference_number_of_disbursement}</td>
                        <td>₹{Number(row.disbursal_amt || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                        <td>{row.approved_by}</td>
                        <td>{row.pin_code}</td>
                        <td>{row.address}</td>
                        <td>{row.state}</td>
                        <td>{row.city}</td>
                        <td>{row.alternate_number}</td>
                        <td>{row.company_code}</td>
                        <td>{row.branch_code}</td>
                      </tr>
                    ))}
                    <tr className="fw-bold table-info">
                      <td colSpan="5" className="text-center">
  Page Totals
</td>

<td>
  ₹{pageTotals.loan_amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
  })}
</td>

<td>
  ₹{pageTotals.demand.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
  })}
</td>

<td></td>
                      <td>₹{pageTotals.total_fess.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      <td colSpan="15"></td>
                      <td>₹{pageTotals.disbursal_amt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      <td colSpan="6"></td>
                    </tr>
                  </>
                ) : (
                  <tr><td colSpan="30" className="text-center">No data found</td></tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-center mt-3">
            <div className="d-flex align-items-center" style={{ gap: "5px" }}>
              <Button color="outline-primary" size="sm" style={buttonSmStyle} 
                onClick={() => fetchDisbursals(currentPage - 1, activeFilters)} 
                disabled={currentPage === 1}>Prev</Button>
              <span className="mx-2">Page {currentPage}</span>
              <Button color="outline-primary" size="sm" style={buttonSmStyle} 
                onClick={() => fetchDisbursals(currentPage + 1, activeFilters)} 
                disabled={!hasNextPage}>Next</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DisbursalReporting;